import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { buyerAdminAPI } from '../../services/adminAPI';

const BuyerAdminDashboard = () => {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('meetings'); // 'meetings' or 'slots'

  const navigate = useNavigate();

  useEffect(() => {
    fetchMeetings();
  }, []);

  const fetchMeetings = async () => {
    try {
      const response = await buyerAdminAPI.getBuyerMeetings();
      setMeetings(response.data.meetings);
    } catch (error) {
      setError('Meetings Loading Error');
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async () => {
    try {
      setSlotsLoading(true);
      const response = await buyerAdminAPI.getBuyerSlots();
      setSlots(response.data.slots);
    } catch (error) {
      setError('Slots Loading Error');
      console.error('Error fetching slots:', error);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleToggleSlot = async (slotId, currentAvailability) => {
    try {
      await buyerAdminAPI.toggleSlotAvailability(slotId, !currentAvailability);
      await fetchSlots(); // Refresh slots
    } catch (error) {
      setError(error.response?.data?.message || 'Error toggling slot availability');
      console.error('Error toggling slot:', error);
    }
  };

  const handleUpdateStatus = async (meetingId, status) => {
    try {
      await buyerAdminAPI.updateMeetingStatus(meetingId, status);
      await fetchMeetings();
    } catch (error) {
      setError(error.response?.data?.message || 'Error updating status');
      console.error('Error updating status:', error);
    }
  };

  const handleCancelMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to cancel this meeting?')) {
      return;
    }
    try {
      await buyerAdminAPI.cancelMeeting(meetingId);
      fetchMeetings(); // Refresh meetings
      fetchSlots(); // Refresh slots
    } catch (error) {
      setError(error.response?.data?.message || 'Error cancelling meeting');
    }
  };

  const handleExportToExcel = async () => {
    try {
      const response = await buyerAdminAPI.exportMeetingsToExcel();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `buyer_meetings_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Error exporting meetings');
      console.error('Error exporting meetings:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#ffc107';
      case 'approved':
        return '#28a745';
      case 'declined':
        return '#dc3545';
      case 'cancelled':
        return '#6c757d';
      default:
        return '#EDB404';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'approved':
        return 'Approved';
      case 'declined':
        return 'Declined';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  const formatTime = (time) => {
    return time ? time.substring(0, 5) : '';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('tr-TR');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        Yükleniyor...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Buyer Dashboard</h1>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#EDB404',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Back to Home
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
          <button
            onClick={() => setError('')}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              color: '#721c24',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        borderBottom: '2px solid #ddd'
      }}>
        <button
          onClick={() => {
            setActiveTab('meetings');
            fetchMeetings();
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: activeTab === 'meetings' ? '#EDB404' : 'transparent',
            color: activeTab === 'meetings' ? 'white' : '#666',
            border: 'none',
            borderBottom: activeTab === 'meetings' ? '3px solid #EDB404' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'meetings' ? 'bold' : 'normal',
            transition: 'all 0.3s ease'
          }}
        >
          My Meetings
        </button>
        <button
          onClick={() => {
            setActiveTab('slots');
            fetchSlots();
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: activeTab === 'slots' ? '#EDB404' : 'transparent',
            color: activeTab === 'slots' ? 'white' : '#666',
            border: 'none',
            borderBottom: activeTab === 'slots' ? '3px solid #EDB404' : '3px solid transparent',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'slots' ? 'bold' : 'normal',
            transition: 'all 0.3s ease'
          }}
        >
          Manage Time Slots
        </button>
      </div>

      {activeTab === 'meetings' && (
        <div style={{
          backgroundColor: '#fff',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {/* Incoming Meetings (from company admins) */}
          <div style={{ marginBottom: '40px' }}>
            <h2>Incoming Meeting Requests</h2>
            {(() => {
              // Buyer'a gelen toplantılar: user_id != buyer'ın user_id ve buyer_id = buyer'ın id
              const incomingMeetings = meetings.filter(meeting => 
                meeting.buyer_id && meeting.user_id !== user?.id
              );
              return incomingMeetings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  No incoming meeting requests yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {incomingMeetings.map((meeting) => (
                    <div
                      key={meeting.id}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '20px',
                        backgroundColor: '#f8f9fa'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>
                            {meeting.company?.name || 'Unknown Company'}
                          </h4>
                          {meeting.user && (
                            <>
                              <p style={{ margin: '5px 0', color: '#666' }}>
                                <strong>Contact:</strong> {meeting.user.name || 'N/A'}
                              </p>
                              <p style={{ margin: '5px 0', color: '#666' }}>
                                <strong>Email:</strong> {meeting.user.email || 'N/A'}
                              </p>
                            </>
                          )}
                          <p style={{ margin: '5px 0', color: '#666' }}>
                            <strong>Date:</strong> {meeting.meeting_slot ? formatDate(meeting.meeting_slot.meeting_date) : 'N/A'}
                          </p>
                          <p style={{ margin: '5px 0', color: '#666' }}>
                            <strong>Time:</strong> {meeting.meeting_slot ? `${formatTime(meeting.meeting_slot.start_time)} - ${formatTime(meeting.meeting_slot.end_time)}` : 'N/A'}
                          </p>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                          <div
                            style={{
                              backgroundColor: getStatusColor(meeting.status),
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}
                          >
                            {getStatusText(meeting.status)}
                          </div>
                          
                          {meeting.status === 'pending' && (
                            <div style={{ display: 'flex', gap: '10px' }}>
                              <button
                                onClick={() => handleUpdateStatus(meeting.id, 'approved')}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(meeting.id, 'declined')}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}
                              >
                                Decline
                              </button>
                            </div>
                          )}
                          
                          {(meeting.status === 'approved') && (
                            <button
                              onClick={() => handleCancelMeeting(meeting.id)}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Sent Meetings (buyer requested from companies) */}
          <div>
            <h2>Sent Meeting Requests</h2>
            {(() => {
              // Buyer'ın gönderdiği toplantılar: user_id = buyer'ın user_id ve buyer_id = buyer'ın id
              // Tip uyumsuzluğunu önlemek için string'e çevir
              const sentMeetings = meetings.filter(meeting => {
                const meetingUserId = String(meeting.user_id);
                const currentUserId = String(user?.id);
                return meetingUserId === currentUserId && meeting.buyer_id;
              });
              return sentMeetings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  No sent meeting requests yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {sentMeetings.map((meeting) => (
                    <div
                      key={meeting.id}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '20px',
                        backgroundColor: '#f8f9fa'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>
                            {meeting.company?.name || 'Unknown Company'}
                          </h4>
                          {/* Company bilgilerini göster (buyer'ın kendi bilgileri değil) */}
                          {meeting.company && (
                            <>
                              {meeting.company.admin && (
                                <>
                                  <p style={{ margin: '5px 0', color: '#666' }}>
                                    <strong>Contact:</strong> {meeting.company.admin.name || 'N/A'}
                                  </p>
                                  <p style={{ margin: '5px 0', color: '#666' }}>
                                    <strong>Email:</strong> {meeting.company.admin.email || meeting.company.email || 'N/A'}
                                  </p>
                                  <p style={{ margin: '5px 0', color: '#666' }}>
                                    <strong>Phone:</strong> {meeting.company.admin.phone || meeting.company.phone || 'N/A'}
                                  </p>
                                </>
                              )}
                            </>
                          )}
                          <p style={{ margin: '5px 0', color: '#666' }}>
                            <strong>Date:</strong> {meeting.meeting_slot ? formatDate(meeting.meeting_slot.meeting_date) : 'N/A'}
                          </p>
                          <p style={{ margin: '5px 0', color: '#666' }}>
                            <strong>Time:</strong> {meeting.meeting_slot ? `${formatTime(meeting.meeting_slot.start_time)} - ${formatTime(meeting.meeting_slot.end_time)}` : 'N/A'}
                          </p>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                          <div
                            style={{
                              backgroundColor: getStatusColor(meeting.status),
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}
                          >
                            {getStatusText(meeting.status)}
                          </div>
                          
                          {meeting.status === 'pending' && (
                            <div style={{
                              padding: '8px 12px',
                              backgroundColor: '#ffc107',
                              color: '#333',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              Waiting for company admin approval
                            </div>
                          )}
                          
                          {meeting.status === 'approved' && (
                            <button
                              onClick={() => handleCancelMeeting(meeting.id)}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {meetings.length > 0 && (
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                onClick={handleExportToExcel}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Export to Excel
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'slots' && (
        <div style={{
          backgroundColor: '#fff',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2>Manage Time Slots</h2>
            <button
              onClick={fetchSlots}
              style={{
                padding: '8px 16px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Refresh
            </button>
          </div>

          {slotsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading slots...
            </div>
          ) : slots.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No slots found.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Time</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Has Meeting</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {slots.map((slot) => {
                    const hasActiveMeeting = slot.meetings && slot.meetings.length > 0;
                    return (
                      <tr key={slot.id} style={{ backgroundColor: slot.is_available ? '#fff' : '#fff3cd' }}>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                          {formatDate(slot.meeting_date)}
                        </td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                          {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                        </td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            backgroundColor: slot.is_available ? '#d4edda' : '#f8d7da',
                            color: slot.is_available ? '#155724' : '#721c24',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {slot.is_available ? 'Available' : 'Closed'}
                          </span>
                        </td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                          {hasActiveMeeting ? (
                            <span style={{ color: '#28a745', fontWeight: 'bold' }}>Yes</span>
                          ) : (
                            <span style={{ color: '#666' }}>No</span>
                          )}
                        </td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                          <button
                            onClick={() => handleToggleSlot(slot.id, slot.is_available)}
                            disabled={!slot.is_available && hasActiveMeeting}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: slot.is_available ? '#dc3545' : '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: (!slot.is_available && hasActiveMeeting) ? 'not-allowed' : 'pointer',
                              fontSize: '12px',
                              opacity: (!slot.is_available && hasActiveMeeting) ? 0.5 : 1
                            }}
                            title={(!slot.is_available && hasActiveMeeting) ? 'Cancel the meeting first to open this slot' : ''}
                          >
                            {slot.is_available ? 'Close Slot' : 'Open Slot'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BuyerAdminDashboard;


